import { css } from "styled-components";
import { theme } from "../theme";
import TickIcon from "../assets/svgIcons/TickIcon";
import CrossIcon from "../assets/svgIcons/CrossIcon";
import { clearTokens, setTokens, setUser } from "../services/authSlice";
import { toast } from "react-toastify";

export const media = Object.keys(theme.breakpoints).reduce((acc, label) => {
  acc[label] = (...args) => css`
    @media (max-width: ${theme.breakpoints[label]}px) {
      ${css(...args)}
    }
  `;
  return acc;
}, {});

export const formatDate = (timestamp) => {
  const date = new Date(timestamp);
  const options = {
    month: "long",
    day: "numeric",
    year: "numeric",
  };

  return new Intl.DateTimeFormat("en-US", options).format(date);
};

export const validatePhone = (phone) => /^\+?\d{0,3}[-\s]?\d{10}$/.test(phone);

export const validateEmail = (email) =>
  /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

export const validateForm = (formData, validationRules) => {
  const validationErrors = {};

  Object.keys(formData).forEach((fieldName) => {
    validationErrors[fieldName] = "";

    validationRules[fieldName]?.some(({ condition, message }) => {
      if (condition(formData[fieldName])) {
        validationErrors[fieldName] = message;
        return true;
      }
      return false;
    });

    if (!validationErrors[fieldName]) {
      delete validationErrors[fieldName];
    }
  });

  return validationErrors;
};

export const stringValidationRules = [
  { condition: (value) => !value, message: "Required" },
];

export const emailValidationRules = [
  { condition: (value) => !value, message: "Required" }
];

export const passwordValidationRules = [
  { condition: (value) => !value, message: "Required" },
  {
    condition: (value) => value.length < 6,
    message: "Password must be at least 6 characters",
  },
];

//Add new validation of 12 characters for password create/update because of old password is in 6 characters
export const passwordUpdationValidationRules = [ 
  { condition: (value) => !value, message: "Required" },
  {
    condition: (value) => value !== value?.trim(),
    message: "Password cannot start or end with a space",
  },
  {
    condition: (value) => value.length < 12,
    message: "Password must be at least 12 characters",
  },
];

export const identityTypes = {
  email: "EMAIL",
  phone: "PHONE",
};

export const status = Object.freeze({
  FAILED: 0,
  OKAY: 1,
  IN_PROGRESS: 2,
});

export const getRandomNumber = (min, max) =>
  Math.floor(Math.random() * (max - min + 1)) + min;

export const convertToSlug = (str) => {
  return str?.toLowerCase()?.replace(/\s+/g, "-");
};

export const getToastIcon = (variant) => {
  switch (variant) {
    case "success":
      return <TickIcon />;
    case "error":
      return <CrossIcon />;
    default:
      return <TickIcon />;
  }
};

export const handleSSO = async (provider = "MICROSOFT", callApi, getUser, getEmployeeListing, dispatch, navigate) => {
  try {
    const result = await callApi(provider);

    window.open(
      result.error.data + `&prompt=${provider = "MICROSOFT" ? 'login' : 'consent'}`,
      '_blank',
      'location=1,status=1,scrollbars=1,width=700,height=700'
    );

    window.onunload = function (res) {
      if (res?.access_token) {
        dispatch(setTokens(res));
        fetchAndProcessUser(getUser, getEmployeeListing, dispatch).then(user => user && navigate("/"))
      }
    };
  } catch (error) {
    console.error('Signup failed:', error);
  }
};

// Helper function to fetch and process the user
export const fetchAndProcessUser = async (getUser, getEmployeeListing, dispatch) => {
  try {
    let { data: user, error: userError } = await getUser();

    if (user?.corporateId) {
      const { data: employee } = await getEmployeeListing({ email: user?.email });
      user = employee?.content?.length && { ...employee?.content?.[0], userId: user.id };
    }

    if (userError || !user) {
      console.error("User error:", userError);
      toast.error(
        userError?.errorMessage || userError?.data?.errorMessage ||
        "Error while fetching user details. Please try again."
      );
      dispatch(clearTokens());
      return null;
    }

    if (user && typeof user === 'object') {
      const { firstName = '', lastName = '' } = user;
      const name = [firstName, lastName].filter(Boolean).join(' ');

      dispatch(
        setUser({
          ...user,
          name,
        })
      );
    }

    return user;
  } catch (error) {
    console.error("Error fetching user:", error);
    toast.error("An unexpected error occurred while fetching user details.");
    return null;
  }
};
